import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabaseClient } from "../utils/supabase";
import type { Dayjs } from "dayjs";

interface CreateEntryParams {
    profileId: string;
    date: Dayjs;
}

interface CreateEntryResult {
    entryId: string;
}

export function useCreateEntry() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ profileId, date }: CreateEntryParams): Promise<CreateEntryResult> => {
            const startOfDay = date.startOf("day").toISOString();
            const startOfNextDay = date.add(1, "day").startOf("day").toISOString();

            // First check if entry already exists for this date
            const { data: existing } = await supabaseClient
                .from("entries")
                .select("entry_id")
                .eq("profile_id", profileId)
                .gte("created_at", startOfDay)
                .lt("created_at", startOfNextDay)
                .maybeSingle();

            // If entry exists, return it
            if (existing?.entry_id) {
                return { entryId: existing.entry_id };
            }

            // Otherwise, create a new entry
            const { data, error } = await supabaseClient
                .from("entries")
                .insert({
                    profile_id: profileId,
                    created_at: startOfDay,
                })
                .select("entry_id")
                .single();

            if (error) {
                throw new Error(`Failed to create entry: ${error.message}`);
            }

            if (!data?.entry_id) {
                throw new Error("Entry created but ID not returned");
            }

            return { entryId: data.entry_id };
        },
        onSuccess: (_, variables) => {
            // Invalidate profile entries to refresh the calendar
            queryClient.invalidateQueries({
                queryKey: ["profile-entries", variables.profileId],
            });
        },
    });
}
